<div class="groups-table-container">
  <div class="table-header">
    <p-iconfield iconPosition="left">
      <p-inputicon>
        <i class="pi pi-search"></i>
      </p-inputicon>
      <input type="text" pInputText placeholder="Search groups..." (input)="onSearch($event)" />
    </p-iconfield>
    <p-button label="Create Group" icon="pi pi-plus" severity="primary" (onClick)="onCreateGroup()"></p-button>
  </div>

  <p-table
    [scrollable]="true"
    [value]="filteredGroups()"
    [loading]="isLoading()"
    [paginator]="true"
    [rows]="10"
    [rowsPerPageOptions]="[10, 25, 50]"
    [tableStyle]="{ 'min-width': '50rem' }"
  >
    <ng-template pTemplate="header">
      <tr>
        @for (col of columns(); track col.field) {
          <th [pSortableColumn]="col.sortable ? col.field : undefined">
            {{ col.header }}
            @if (col.sortable) {
              <p-sortIcon></p-sortIcon>
            }
          </th>
        }
      </tr>
    </ng-template>
    <ng-template pTemplate="body" let-group>
      <tr>
        <td>
          <strong>{{ group.name }}</strong>
          @if (group.description) {
            <br />
            <small class="secondary-text">{{ group.description }}</small>
          }
        </td>
        <td>
          <div class="payment-account-info">
            <strong>{{ group.paymentAccountName }}</strong>
            @if (group.paymentAccountEmail) {
              <br />
              <small class="secondary-text">{{ group.paymentAccountEmail }}</small>
            }
          </div>
        </td>
        <td><p-tag severity="secondary" [value]="getGroupTypeLabel(group.groupType)"></p-tag></td>
        <td>
          @if (group.accountCount > 0) {
            <p-tag
              severity="secondary"
              [value]="group.accountCount + ' account' + (group.accountCount !== 1 ? 's' : '')"
            ></p-tag>
          } @else {
            <span class="secondary-text">No accounts</span>
          }
        </td>
        <td>
          @if (group.members.length > 0) {
            <p-tag
              severity="info"
              pTooltip="Click to manage members"
              [value]="group.members.length + ' member' + (group.members.length !== 1 ? 's' : '')"
              [style]="{ cursor: 'pointer' }"
              (click)="onManageMembers(group)"
            ></p-tag>
          } @else {
            <span class="secondary-text link" pTooltip="Click to add members" (click)="onManageMembers(group)">
              No members
            </span>
          }
        </td>
        <td>
          <p-tag [value]="getStatusLabel(group.isActive)" [severity]="getStatusSeverity(group.isActive)"></p-tag>
        </td>
        <td>{{ formatDate(group.createdOn) }}</td>
        <td>
          <div class="action-buttons">
            <p-button
              icon="pi pi-eye"
              severity="info"
              pTooltip="View household"
              tooltipPosition="top"
              [text]="true"
              [rounded]="true"
              [routerLink]="['/access-management/households', group.groupId]"
            ></p-button>
            <p-button
              icon="pi pi-users"
              severity="info"
              pTooltip="Manage Members"
              tooltipPosition="top"
              [text]="true"
              [rounded]="true"
              (onClick)="onManageMembers(group)"
            ></p-button>
            <p-button
              icon="pi pi-pencil"
              severity="info"
              pTooltip="Edit"
              tooltipPosition="top"
              [text]="true"
              [rounded]="true"
              (onClick)="onEditGroup(group)"
            ></p-button>
            <p-button
              icon="pi pi-trash"
              severity="danger"
              pTooltip="Delete"
              tooltipPosition="top"
              [text]="true"
              [rounded]="true"
              [loading]="deletingId() === group.groupId"
              (onClick)="onDeleteGroup(group)"
            ></p-button>
          </div>
        </td>
      </tr>
    </ng-template>
    <ng-template pTemplate="emptymessage">
      <tr>
        <td [attr.colspan]="columns().length">
          <div class="empty-state">
            <i class="pi pi-users large-icon"></i>
            <p>No groups found</p>
            @if (searchTerm()) {
              <small>Try adjusting your search criteria</small>
            } @else {
              <small>Create your first group to get started</small>
            }
          </div>
        </td>
      </tr>
    </ng-template>
  </p-table>
</div>

<!-- Create/Edit Group Dialog -->
<p-dialog
  [header]="dialogTitle()"
  [modal]="true"
  [style]="{ width: '50vw' }"
  [draggable]="false"
  [resizable]="false"
  [(visible)]="showDialog"
>
  <div class="dialog-content">
    <!--
      Only creation sends these four. Access Management has no endpoint that changes a group's payment
      account, description, type, or active flag, so showing them in Edit mode offered edits the save
      path silently dropped while still reporting success.
    -->
    @if (isCreateMode()) {
      <div class="form-field">
        <label for="paymentAccountId">Payment Account *</label>
        <p-select
          id="paymentAccountId"
          name="paymentAccountId"
          optionLabel="name"
          optionValue="accountId"
          placeholder="Select payment account"
          appendTo="body"
          [ngModel]="groupForm.paymentAccountId().value()"
          [options]="accounts()"
          [filter]="true"
          [style]="{ width: '100%' }"
          (ngModelChange)="groupForm.paymentAccountId().value.set($event)"
        >
          <ng-template let-account pTemplate="item">
            <div class="dropdown-item">
              <strong>{{ account.name }}</strong>
              @if (account.user) {
                <small class="secondary-text">{{ account.user.name }} ({{ account.user.email }})</small>
              }
            </div>
          </ng-template>
        </p-select>
        @let paymentAccountIdField = groupForm.paymentAccountId();
        @if (paymentAccountIdField.touched() && !paymentAccountIdField.valid()) {
          <small class="p-error">
            @for (error of paymentAccountIdField.errors(); track error.kind) {
              {{ error.message }}
            }
          </small>
        }
      </div>
    }

    <div class="form-field">
      <label for="name">Group Name *</label>
      <input
        class="field-input"
        id="name"
        type="text"
        pInputText
        placeholder="Enter group name"
        [formField]="groupForm.name"
      />
      @let nameField = groupForm.name();
      @if (nameField.touched() && !nameField.valid()) {
        <small class="p-error">
          @for (error of nameField.errors(); track error.kind) {
            {{ error.message }}
          }
        </small>
      }
    </div>

    @if (isCreateMode()) {
      <div class="form-field">
        <label for="description">Description</label>
        <textarea
          class="field-input"
          id="description"
          placeholder="Enter group description"
          rows="3"
          [formField]="groupForm.description"
        ></textarea>
      </div>

      <div class="form-field">
        <label for="groupType">Group Type *</label>
        <p-select
          id="groupType"
          name="groupType"
          optionLabel="label"
          optionValue="value"
          placeholder="Select group type"
          appendTo="body"
          [ngModel]="groupForm.groupType().value()"
          [options]="groupTypeOptions"
          [style]="{ width: '100%' }"
          (ngModelChange)="groupForm.groupType().value.set($event)"
        ></p-select>
      </div>

      <div class="form-field checkbox-field">
        <label>
          <input type="checkbox" [formField]="groupForm.isActive" />
          <span>Active (group is operational)</span>
        </label>
      </div>
    }
  </div>

  <ng-template pTemplate="footer">
    <p-button label="Cancel" severity="secondary" [disabled]="saving()" (onClick)="showDialog.set(false)"></p-button>
    <p-button
      [label]="dialogButtonLabel()"
      [loading]="saving()"
      [disabled]="!isFormValid()"
      (onClick)="onSaveGroup()"
    ></p-button>
  </ng-template>
</p-dialog>

<!-- Manage Members Dialog -->
<p-dialog
  [header]="'Manage Members for ' + (selectedGroupForMembers()?.name || '')"
  [modal]="true"
  [style]="{ width: '70vw' }"
  [draggable]="false"
  [resizable]="false"
  [(visible)]="showMembersDialog"
>
  <div class="members-management">
    <!-- Associated Accounts Section -->
    <div class="accounts-section">
      <h4>Associated Accounts ({{ groupAccounts().length }})</h4>
      @if (groupAccounts().length > 0) {
        <div class="accounts-tags">
          @for (account of groupAccounts(); track account.accountId) {
            <p-tag
              tooltipPosition="top"
              [value]="account.name"
              [severity]="account.accountId === selectedGroupForMembers()?.paymentAccountId ? 'success' : 'secondary'"
              [pTooltip]="account.accountId === selectedGroupForMembers()?.paymentAccountId ? 'Payment Account' : ''"
            ></p-tag>
          }
        </div>
        <small class="secondary-text account-info">
          <i class="pi pi-info-circle"></i>
          Payment account:
          <strong>{{ paymentAccountName() }}</strong>
        </small>
      } @else {
        <p class="secondary-text no-margin">No accounts associated with this group yet</p>
      }
    </div>

    <div class="invite-member-section">
      <h4>Invite Member</h4>
      @if (inviterOptions().length > 0) {
        <div class="invite-member-form">
          <div class="form-row">
            <div class="form-field flex-1">
              <label for="inviteEmail">Email</label>
              <input
                id="inviteEmail"
                pInputText
                type="email"
                placeholder="person@example.com"
                [style]="{ width: '100%' }"
                [(ngModel)]="inviteEmail"
              />
            </div>
            <div class="form-field flex-1">
              <label for="inviteHouseholdRelationship">Household relationship</label>
              <p-select
                inputId="inviteHouseholdRelationship"
                optionLabel="label"
                optionValue="value"
                appendTo="body"
                [options]="inviteHouseholdRelationshipOptions"
                [style]="{ width: '100%' }"
                [(ngModel)]="inviteHouseholdRelationship"
              ></p-select>
            </div>
            <div class="form-field flex-1">
              <label for="inviteRole">Role</label>
              <p-select
                inputId="inviteRole"
                optionLabel="label"
                optionValue="value"
                appendTo="body"
                [options]="inviteRoleOptions()"
                [style]="{ width: '100%' }"
                [(ngModel)]="inviteRoleName"
              ></p-select>
            </div>
            <div class="form-field flex-1">
              <label for="inviteInviter">Sent by</label>
              <p-select
                inputId="inviteInviter"
                optionLabel="label"
                optionValue="value"
                placeholder="Select member"
                appendTo="body"
                [options]="inviterOptions()"
                [style]="{ width: '100%' }"
                [(ngModel)]="inviteInviterUserId"
              ></p-select>
            </div>
            <div class="form-field">
              <p-button
                label="Stage invitation"
                icon="pi pi-envelope"
                [disabled]="!inviteEmail() || !inviteInviterUserId() || inviting()"
                (onClick)="onInviteMember()"
              ></p-button>
            </div>
          </div>
          <small class="secondary-text">
            Joining a household is by invitation only. Staging does not send anything: the household buys a seat against
            this email address, and that payment is what sends the invitation. The invitee then accepts from their own
            email, and an address that already has a Youtopia account cannot be invited. The role applies once they
            accept: the Admin bundle carries ordering, meal planning and the household wallet, and not inviting, which
            the household can grant a member on its own.
          </small>
        </div>
      } @else {
        <p class="secondary-text no-margin">{{ inviterAbsenceReason() }}</p>
      }
    </div>

    <div class="members-list-section">
      <h4>Current Members ({{ groupMembers().length }})</h4>
      @if (membersLoading()) {
        <div class="loading-state"><p-progressSpinner></p-progressSpinner></div>
      } @else if (membersWithDetails().length > 0) {
        <div class="members-list">
          @for (member of membersWithDetails(); track member.user.userId) {
            <div class="member-item">
              <div class="member-info">
                <div class="user-details">
                  <strong>{{ member.user.name }}</strong>
                  <small class="secondary-text">{{ member.user.email }}</small>
                </div>
                <div class="role-badge">
                  <p-tag severity="info" [value]="member.role.name"></p-tag>
                  <p-tag severity="secondary" [value]="member.householdRelationship"></p-tag>
                </div>
              </div>
              <div class="member-actions">
                @if (member.canChangeRole) {
                  <p-select
                    appendTo="body"
                    optionLabel="label"
                    optionValue="value"
                    styleClass="role-select"
                    [options]="assignableRoleOptions()"
                    [ngModel]="selectedRoleName(member)"
                    [disabled]="changingRoleUserId() === member.user.userId"
                    [ariaLabel]="'Role for ' + member.user.name"
                    (ngModelChange)="onStageRole(member, $event)"
                  ></p-select>
                  <p-button
                    icon="pi pi-check"
                    size="small"
                    severity="info"
                    pTooltip="Apply role"
                    tooltipPosition="left"
                    [ariaLabel]="'Apply role for ' + member.user.name"
                    [text]="true"
                    [rounded]="true"
                    [disabled]="!hasStagedRoleChange(member)"
                    [loading]="changingRoleUserId() === member.user.userId"
                    (onClick)="onApplyRole(member)"
                  ></p-button>
                }
                <p-button
                  icon="pi pi-times"
                  severity="danger"
                  size="small"
                  pTooltip="Remove member"
                  tooltipPosition="left"
                  [text]="true"
                  [rounded]="true"
                  (onClick)="onRemoveMember(member.user.userId)"
                ></p-button>
              </div>
            </div>
          }
        </div>
      } @else {
        <div class="empty-state-small">
          <i class="pi pi-users"></i>
          <p>No members in this group yet</p>
        </div>
      }
    </div>
  </div>

  <ng-template pTemplate="footer">
    <p-button label="Close" severity="secondary" (onClick)="showMembersDialog.set(false)"></p-button>
  </ng-template>
</p-dialog>

<app-delete-entity-dialog
  entityKind="household"
  [entityName]="householdPendingDelete()?.name ?? ''"
  [submitting]="deletingId() === householdPendingDelete()?.groupId"
  [(visible)]="showDeleteDialog"
  (confirm)="onConfirmDeleteHousehold($event)"
></app-delete-entity-dialog>
